import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { DatasetsApiActions, DatasetsUiActions } from '../actions';
import { catchError, map, mergeMap, of, tap } from 'rxjs';
import { ToastService } from '../../../../shared/services/toast.service';

import { DatasetService } from '../../service/dataset.service';
import { AnalyticalDatasetService } from '../../service/analytical-dataset.service';
import { IDatasetFile } from '../../models/Dataset';
import { IAnalyticalDataset, IAnalyticalDatasetListResponse } from '../../models/AnalyticalDataset';
import { DialogService } from '../../../../shared/services/dialog.service';
import { DELETE_ANALYTICAL_DATASET_MODAL_ID } from '../../constants/modal-constants';

@Injectable()
export class DatasetsEffects {
  private actions$ = inject(Actions);
  private toastService = inject(ToastService);
  private datasetService = inject(DatasetService);
  private analyticalDatasetService = inject(AnalyticalDatasetService);
  private dialogService = inject(DialogService);

  getDatasetFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetsUiActions.GetDatasetFiles),
      mergeMap(({ projectId }) =>
        this.datasetService.getDatasetFiles(projectId).pipe(
          map((datasetFiles) => DatasetsApiActions.getDatasetFilesSuccess({ payload: datasetFiles as IDatasetFile[] })),
          catchError((error) => {
            this.toastService.show(`Error loading dataset files: ${error.message || 'Unexpected error'}`, 'error');
            return of(DatasetsApiActions.getDatasetFilesError({ errors: error }));
          })
        )
      )
    )
  );

  patchDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetsUiActions.PatchDataset),
      mergeMap(({ datasetId, description }) =>
        this.datasetService.patchDataset(datasetId, description).pipe(
          map(() => DatasetsApiActions.patchDatasetSuccess({ payload: description })),
          catchError((error) => {
            this.toastService.show(`Error updating dataset: ${error.message || 'Unexpected error'}`, 'error');
            return of(DatasetsApiActions.patchDatasetError({ errors: error }));
          })
        )
      )
    )
  );

  postGrantDatasetAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetsUiActions.PostGrantDatasetAccess),
      mergeMap(({ datasetId, projectId }) =>
        this.datasetService.postGrantDatasetAccess(datasetId, projectId).pipe(
          tap(() => {
            this.toastService.show(`Dataset access granted successfully`, 'success');
          }),
          map(() => DatasetsApiActions.postGrantDatasetAccessSuccess({ projectId })),
          catchError((error) => {
            this.toastService.show(`Error granting dataset access: ${error.message || 'Unexpected error'}`, 'error');
            return of(DatasetsApiActions.postGrantDatasetAccessError({ errors: error }));
          })
        )
      )
    )
  );

  getAndSelectDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetsUiActions.GetDatasetById),
      mergeMap(({ datasetId }) =>
        this.datasetService.getAndSelectDataset(datasetId).pipe(
          map((dataset) => DatasetsApiActions.getAndSelectDatasetSuccess({ payload: dataset as IDatasetFile })),
          catchError((error) => {
            this.toastService.show(`Error loading dataset: ${error.message || 'Unexpected error'}`, 'error');
            return of(DatasetsApiActions.getAndSelectDatasetError({ errors: error }));
          })
        )
      )
    )
  );

  getAnalyticalDatasets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetsUiActions.GetAnalyticalDatasets),
      mergeMap(({ params }) =>
        this.analyticalDatasetService.getAnalyticalDatasets(params).pipe(
          map((response) => {
            // Handle both paginated and non-paginated responses
            const datasets = Array.isArray(response) ? response : (response as IAnalyticalDatasetListResponse).results;
            return DatasetsApiActions.getAnalyticalDatasetsSuccess({ payload: datasets as IAnalyticalDataset[] });
          }),
          catchError((error) => {
            this.toastService.show(
              `Error loading analytical datasets: ${error.message || 'Unexpected error'}`,
              'error'
            );
            return of(DatasetsApiActions.getAnalyticalDatasetsError({ errors: error }));
          })
        )
      )
    )
  );

  deleteAnalyticalDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetsUiActions.DeleteAnalyticalDataset),
      mergeMap(({ datasetId }) =>
        this.analyticalDatasetService.deleteAnalyticalDataset(datasetId).pipe(
          map(() => {
            // Deletion is now asynchronous: the API returns 202 and a Celery
            // task tears down the dataset. Completion is surfaced via an
            // in-app/email notification; the row is dropped optimistically on
            // success (see LabProjectDetailsComponent). The loader interceptor's
            // `finalize` clears the global overlay when this request settles.
            this.toastService.show('Dataset deletion started', 'info');
            this.dialogService.close(DELETE_ANALYTICAL_DATASET_MODAL_ID);
            return DatasetsApiActions.deleteAnalyticalDatasetSuccess({ datasetId });
          }),
          catchError((error) => {
            const detail = error?.error?.error || error?.error?.detail || error?.message || 'Unexpected error';
            this.toastService.show(`Error deleting dataset: ${detail}`, 'error');
            this.dialogService.close(DELETE_ANALYTICAL_DATASET_MODAL_ID);
            return of(DatasetsApiActions.deleteAnalyticalDatasetError({ errors: error }));
          })
        )
      )
    )
  );

  // Refresh analytical datasets after successful deletion
  refreshAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetsApiActions.deleteAnalyticalDatasetSuccess),
      map(() => DatasetsUiActions.GetAnalyticalDatasets({ params: undefined }))
    )
  );
}
